// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which Rail panel is open. Remembered across launches so the Rail
// comes back the way it was left.

import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

export type RailKey = 'chats' | 'today' | 'calendar' | 'tasks' | 'scratchpad' | 'assistant';

export const RAIL_KEYS: ReadonlyArray<RailKey> = ['chats', 'today', 'calendar', 'tasks', 'scratchpad', 'assistant'];

const STORAGE_KEY = 'konvo_rail.active_panel';

export type RailDetail = {kind: 'mail' | 'task' | 'event' | 'chat' | 'whatsapp'; id: string};

function readStored(): RailKey | null {
	try {
		const value = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
		// The separate WhatsApp and Mail panels were folded into Chats.
		// Keep existing installations on a useful panel after the upgrade.
		if (value === 'whatsapp' || value === 'mail') return 'chats';
		return value && (RAIL_KEYS as ReadonlyArray<string>).includes(value) ? (value as RailKey) : null;
	} catch {
		return null;
	}
}

class RailStateStore {
	activePanel: RailKey | null = readStored();
	/** An item opened inside the active panel; null shows the list. */
	detail: RailDetail | null = null;
	/** Auto-hide: collapsed shows only the chevron tab; pinned keeps it open. */
	collapsed = KonvoCalendarPrefs.railAutoHide;
	pinned = false;
	private hideTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'hideTimer'>(this, {hideTimer: false}, {autoBind: true});
	}

	get autoHide(): boolean {
		return KonvoCalendarPrefs.railAutoHide;
	}

	get autoHideDelayMs(): number {
		return Math.max(2, KonvoCalendarPrefs.railAutoHideDelay) * 1000;
	}

	/** Hover on the tab: open, and hide again once the pointer has been away for the delay. */
	peek(): void {
		this.collapsed = false;
		this.pinned = false;
	}

	/** Click: open and stay open until collapsed on purpose. */
	pin(): void {
		this.cancelHide();
		this.collapsed = false;
		this.pinned = true;
	}

	collapse(): void {
		this.cancelHide();
		this.collapsed = true;
		this.pinned = false;
	}

	/** Pointer left the Rail (or it lost focus): start the countdown unless pinned. */
	scheduleHide(): void {
		if (!this.autoHide || this.pinned || this.collapsed) return;
		this.cancelHide();
		this.hideTimer = setTimeout(() => {
			this.hideTimer = null;
			if (!this.pinned) this.collapse();
		}, this.autoHideDelayMs);
	}

	cancelHide(): void {
		if (this.hideTimer) clearTimeout(this.hideTimer);
		this.hideTimer = null;
	}

	/** The setting turned off while collapsed: come back; turned on: start collapsed. */
	syncWithSetting(): void {
		if (!this.autoHide) {
			this.cancelHide();
			this.collapsed = false;
			this.pinned = false;
		} else if (!this.pinned) {
			this.collapsed = true;
		}
	}

	toggle(key: RailKey): void {
		this.setActivePanel(this.activePanel === key ? null : key);
	}

	close(): void {
		this.setActivePanel(null);
	}

	openDetail(detail: RailDetail): void {
		this.detail = detail;
	}

	closeDetail(): void {
		this.detail = null;
	}

	/** Back from a detail: Home opens chats, mail and tasks too, so say where Back goes. */
	backLabel(fallback: string): string {
		return this.activePanel === 'today' ? 'Back to Home' : fallback;
	}

	private setActivePanel(key: RailKey | null): void {
		this.activePanel = key;
		this.detail = null;
		try {
			if (key) {
				getProtectedLocalStorage()?.setItem(STORAGE_KEY, key);
			} else {
				getProtectedLocalStorage()?.removeItem(STORAGE_KEY);
			}
		} catch {
			// Storage may be unavailable; the Rail still works for this session.
		}
	}
}

export default new RailStateStore();
